import React, { useState, useEffect, useCallback } from "react";

// CB-07 / Council Master Build phase B3 — "Business Identity & Assets" panel.
//
// The operator surface for the Client Asset Library (the KEYSTONE). One panel
// on the client-edit screen that captures the real, structured client data
// every downstream enrichment reads from (services/client-assets.js resolver,
// B2) and validates against (claim gate, localness gate, schema steps):
//
//   • Verified Facts   → clients.verified_facts (typed, gated)
//   • Author / Byline  → clients.author_* columns (Article/Person schema)
//   • Experts          → client_experts      (E2 expert quotes → Person schema)
//   • Testimonials     → client_testimonials (E1 testimonial injection)
//   • Job Stories      → client_job_stories  (E1 micro case-study injection)
//   • Tools            → client_tools        (E2 contextual tool CTAs)
//   • Geo              → client_geo          (F1 geo-enrichment / F2 gate)
//   • Image sources    → image_library.source (E3 real-photo preference)
//
// Persistence seams (all existing/additive — the generator NEVER invents this):
//   verified_facts  → PUT  /api/clients/:id/verified-facts (typed+validated)
//   author block    → PUT  /api/clients/:id                (column passthrough)
//   five lists      → GET/POST/PUT/DELETE /api/clients/:id/assets[/:type[/:row]]
//   image flags     → PUT  /api/images/:id                 (source/service/loc)
//
// DEGRADE GRACEFULLY: empty sections are normal (a client may have no data
// yet) — the panel renders empty-state copy and never blocks. Each section
// saves independently of the parent edit form's "Save Changes".
//
// Props:
//   client    — selectedClient object (source of author_* + verified_facts)
//   api       — backend base URL
//   authFetch — auth-injecting fetch wrapper from App.js

// Verified-facts vocabulary (mirrors backend VERIFIED_FACT_TYPES). The GET
// endpoint also returns fact_types/fact_keys; we prefer those at runtime and
// fall back to this so the form still renders if the call degrades.
const FACT_TYPES_FALLBACK = {
  offers_free_estimates: "boolean",
  offers_financing: "boolean",
  available_24_7: "boolean",
  emergency_service: "boolean",
  warranty: "string",
  years_in_business: "int",
  licensed: "boolean",
  license_number: "string",
  insured: "boolean",
  satisfaction_guarantee: "boolean",
  service_radius_miles: "int",
};
const FACT_LABELS = {
  offers_free_estimates: "Free estimates",
  offers_financing: "Financing offered",
  available_24_7: "Available 24/7",
  emergency_service: "Emergency service",
  warranty: "Warranty terms",
  years_in_business: "Years in business",
  licensed: "Licensed",
  license_number: "License number",
  insured: "Insured",
  satisfaction_guarantee: "Satisfaction guarantee",
  service_radius_miles: "Service radius (miles)",
};

// Field schemas drive the generic list editors. `type`:
//   text | textarea | date — scalar inputs
//   array — comma-separated text in the UI; backend coerces to text[]
const LIST_SCHEMAS = {
  experts: {
    title: "Authors & Experts",
    blurb: "Named technicians/experts whose real credentials are the E-E-A-T signal. Quoted in posts (→ Person schema).",
    addLabel: "+ Add expert",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "e.g. Dave Whitfield" },
      { key: "title", label: "Title / Role", type: "text", placeholder: "e.g. Lead HVAC Technician" },
      { key: "certs", label: "Certifications", type: "array", placeholder: "NATE-certified, EPA 608" },
      { key: "expertise", label: "Expertise topics", type: "array", placeholder: "ac_install, heat_pumps" },
      { key: "bio", label: "Short bio", type: "textarea", placeholder: "Factual one-liner for the byline / Person schema." },
    ],
  },
  testimonials: {
    title: "Testimonials",
    blurb: "Real customer testimonials. One relevant excerpt is injected per post (matched by service / location).",
    addLabel: "+ Add testimonial",
    fields: [
      { key: "quote", label: "Quote", type: "textarea", required: true, placeholder: "What the customer actually said." },
      { key: "attribution", label: "Attribution", type: "text", placeholder: "e.g. Maria G., Mount Holly NC" },
      { key: "service_type", label: "Service type", type: "text", placeholder: "ac_install" },
      { key: "location", label: "Location slug", type: "text", placeholder: "mount_holly_nc" },
    ],
  },
  job_stories: {
    title: "Job Stories",
    blurb: "Real problem → solution → outcome micro case studies. The strongest first-hand Experience signal.",
    addLabel: "+ Add job story",
    fields: [
      { key: "problem", label: "Problem", type: "textarea", required: true, placeholder: "The customer's presenting problem." },
      { key: "solution", label: "Solution", type: "textarea", placeholder: "What the crew did." },
      { key: "outcome", label: "Outcome", type: "text", placeholder: 'e.g. "cut the bill 30%"' },
      { key: "service_type", label: "Service type", type: "text", placeholder: "ac_install" },
      { key: "location", label: "Location slug", type: "text", placeholder: "mount_holly_nc" },
      { key: "tech_name", label: "Technician", type: "text", placeholder: "ties to an expert by name" },
      { key: "job_date", label: "Job date", type: "date" },
    ],
  },
  tools: {
    title: "Tools & Calculators",
    blurb: "Interactive tools surfaced as a contextual CTA when a post's topic overlaps the tool's topics.",
    addLabel: "+ Add tool",
    fields: [
      { key: "url", label: "URL", type: "text", required: true, placeholder: "https://site.com/seer-calculator" },
      { key: "name", label: "Name", type: "text", placeholder: "SEER Savings Calculator" },
      { key: "cta_text", label: "CTA text", type: "text", placeholder: "Estimate your SEER savings" },
      { key: "topics", label: "Topics", type: "array", placeholder: "seer, savings, ac_install" },
    ],
  },
  geo: {
    title: "Geo Facts",
    blurb: "Per-service-area facts that make local posts unmistakably local. F2 gate requires ≥2 geo refs on local posts.",
    addLabel: "+ Add service area",
    fields: [
      { key: "service_area_id", label: "Service area slug", type: "text", required: true, placeholder: "mount_holly_nc" },
      { key: "climate", label: "Climate", type: "textarea", placeholder: "humid subtropical; ice storms over hard freezes" },
      { key: "utility_rebates", label: "Utility / rebates", type: "textarea", placeholder: "Duke Energy heat-pump rebate up to $..." },
      { key: "building_stock", label: "Building stock", type: "textarea", placeholder: "1960s brick ranch, original ductwork common" },
      { key: "regional_refs", label: "Regional references", type: "array", placeholder: "Catawba River, US-29 corridor" },
    ],
  },
};
const LIST_TYPES = Object.keys(LIST_SCHEMAS);

// Normalize an API row into editable form values: array columns join to a
// comma string so the <input> can hold them; the backend splits them back.
function rowForEdit(type, row) {
  const out = { ...row };
  for (const f of LIST_SCHEMAS[type].fields) {
    if (f.type === "array") {
      out[f.key] = Array.isArray(row[f.key]) ? row[f.key].join(", ") : (row[f.key] || "");
    } else {
      out[f.key] = row[f.key] == null ? "" : row[f.key];
    }
  }
  return out;
}

export default function BusinessIdentityAssetsPanel({ client, api, authFetch }) {
  const clientId = client?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // verified_facts: key → editable value ('yes'/'no'/'' for booleans, string
  // otherwise). factTypes drives which input renders per key.
  const [factTypes, setFactTypes] = useState(FACT_TYPES_FALLBACK);
  const [facts, setFacts] = useState({});
  const [savingFacts, setSavingFacts] = useState(false);

  // author block
  const [author, setAuthor] = useState({
    author_name: "", author_title: "", author_credentials: "", author_bio: "", author_headshot_url: "",
  });
  const [savingAuthor, setSavingAuthor] = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);

  // five list tables
  const [lists, setLists] = useState({ experts: [], testimonials: [], job_stories: [], tools: [], geo: [] });
  const [geoFilling, setGeoFilling] = useState(null); // idx of the geo row being auto-filled

  // image source-flagging
  const [images, setImages] = useState([]);

  // per-section transient status: { [sectionKey]: { ok, msg } }
  const [status, setStatus] = useState({});
  const flash = useCallback((key, ok, msg) => {
    setStatus(s => ({ ...s, [key]: { ok, msg } }));
  }, []);

  // ── load everything ────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const [vfRes, asRes, imgRes] = await Promise.all([
        authFetch(`${api}/api/clients/${clientId}/verified-facts`),
        authFetch(`${api}/api/clients/${clientId}/assets`),
        authFetch(`${api}/api/images/client/${clientId}`),
      ]);

      // verified facts (required for the section, but non-fatal overall)
      if (vfRes.ok) {
        const d = await vfRes.json();
        const types = d.fact_types && typeof d.fact_types === "object" ? d.fact_types : FACT_TYPES_FALLBACK;
        setFactTypes(types);
        const raw = d.verified_facts && typeof d.verified_facts === "object" ? d.verified_facts : {};
        const form = {};
        for (const key of Object.keys(types)) {
          const entry = raw[key];
          const val = entry && typeof entry === "object" ? entry.value : entry;
          if (types[key] === "boolean") form[key] = val === true ? "yes" : val === false ? "no" : "";
          else form[key] = val == null ? "" : String(val);
        }
        setFacts(form);
      }

      // asset lists
      if (asRes.ok) {
        const d = await asRes.json();
        const a = d.assets || {};
        const next = {};
        for (const t of LIST_TYPES) {
          next[t] = Array.isArray(a[t]) ? a[t].map(r => rowForEdit(t, r)) : [];
        }
        setLists(next);
      }

      // image library
      if (imgRes.ok) {
        const d = await imgRes.json();
        const imgs = Array.isArray(d) ? d : (Array.isArray(d.images) ? d.images : []);
        setImages(imgs.map(i => ({
          ...i,
          source: i.source || "stock",
          service_type: i.service_type || "",
          location_context: i.location_context || "",
        })));
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [api, authFetch, clientId]);

  useEffect(() => { load(); }, [load]);

  // author block is sourced from the client row (already loaded by App.js).
  useEffect(() => {
    setAuthor({
      author_name: client?.author_name || "",
      author_title: client?.author_title || "",
      author_credentials: client?.author_credentials || "",
      author_bio: client?.author_bio || "",
      author_headshot_url: client?.author_headshot_url || "",
    });
  }, [client]);

  // ── verified facts save ────────────────────────────────────────────
  // Build the merge patch from form state. Booleans: yes→true, no→false,
  // ""→"" (unverify). Scalars: value or "" (unverify). Backend type-validates.
  const saveFacts = async () => {
    if (!clientId) return;
    setSavingFacts(true);
    flash("facts", true, "");
    try {
      const patch = {};
      for (const key of Object.keys(factTypes)) {
        const v = facts[key];
        if (factTypes[key] === "boolean") {
          patch[key] = v === "yes" ? true : v === "no" ? false : "";
        } else {
          patch[key] = v == null ? "" : String(v).trim();
        }
      }
      const res = await authFetch(`${api}/api/clients/${clientId}/verified-facts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verified_facts: patch }),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      flash("facts", true, "Saved.");
    } catch (e) {
      flash("facts", false, e.message || "Save failed");
    } finally {
      setSavingFacts(false);
    }
  };

  // ── author block save ──────────────────────────────────────────────
  const saveAuthor = async () => {
    if (!clientId) return;
    setSavingAuthor(true);
    flash("author", true, "");
    try {
      const res = await authFetch(`${api}/api/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(author),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      flash("author", true, "Saved.");
    } catch (e) {
      flash("author", false, e.message || "Save failed");
    } finally {
      setSavingAuthor(false);
    }
  };

  // ── headshot upload ────────────────────────────────────────────────
  // Uploads the chosen image to the backend, which stores it and writes
  // clients.author_headshot_url. Upload IS the save for the photo — the URL
  // comes back and we reflect it in the preview + the URL field. Don't send
  // a Content-Type header: the browser sets the multipart boundary itself.
  const uploadHeadshot = async (file) => {
    if (!clientId || !file) return;
    setUploadingHeadshot(true);
    flash("author", true, "");
    try {
      const fd = new FormData();
      fd.append("headshot", file);
      const res = await authFetch(`${api}/api/clients/${clientId}/headshot`, {
        method: "POST",
        body: fd,
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      setAuthor(p => ({ ...p, author_headshot_url: d.author_headshot_url || "" }));
      flash("author", true, "Headshot uploaded.");
    } catch (e) {
      flash("author", false, e.message || "Headshot upload failed");
    } finally {
      setUploadingHeadshot(false);
    }
  };

  // ── list helpers ───────────────────────────────────────────────────
  const setRow = (type, idx, patch) => {
    setLists(prev => {
      const arr = prev[type].slice();
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [type]: arr };
    });
  };
  const addRow = (type) => {
    const blank = { _draft: true, _key: `draft-${type}-${Date.now()}` };
    for (const f of LIST_SCHEMAS[type].fields) blank[f.key] = "";
    setLists(prev => ({ ...prev, [type]: [...prev[type], blank] }));
  };
  const removeRow = async (type, idx) => {
    const row = lists[type][idx];
    // unsaved draft → drop locally, no API call.
    if (!row.id) {
      setLists(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== idx) }));
      return;
    }
    try {
      const res = await authFetch(`${api}/api/clients/${clientId}/assets/${type}/${row.id}`, { method: "DELETE" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      setLists(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== idx) }));
      flash(`${type}-${idx}`, true, "Removed.");
    } catch (e) {
      flash(`${type}-${idx}`, false, e.message || "Delete failed");
    }
  };
  const saveRow = async (type, idx) => {
    const schema = LIST_SCHEMAS[type];
    const row = lists[type][idx];
    const missing = schema.fields.find(f => f.required && !String(row[f.key] || "").trim());
    if (missing) { flash(`${type}-${idx}`, false, `${missing.label} is required`); return; }
    const body = {};
    for (const f of schema.fields) body[f.key] = row[f.key] == null ? "" : row[f.key];
    setRow(type, idx, { _saving: true });
    try {
      const url = row.id
        ? `${api}/api/clients/${clientId}/assets/${type}/${row.id}`
        : `${api}/api/clients/${clientId}/assets/${type}`;
      const res = await authFetch(url, {
        method: row.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      const saved = rowForEdit(type, d.row || {});
      setRow(type, idx, { ...saved, _saving: false, _draft: false });
      flash(`${type}-${idx}`, true, "Saved.");
    } catch (e) {
      setRow(type, idx, { _saving: false });
      flash(`${type}-${idx}`, false, e.message || "Save failed");
    }
  };

  // ── geo facts auto-fill ────────────────────────────────────────────
  // Reads the row's service-area slug, asks the backend to generate accurate
  // local context (climate / utility / building stock / landmarks), and fills
  // the row for operator review. Does NOT save — the operator still reviews +
  // clicks "Save new". The AI call runs server-side (valid key on Railway).
  const autofillGeo = async (idx) => {
    const row = lists.geo[idx];
    const slug = String((row && row.service_area_id) || "").trim();
    if (!slug) { flash(`geo-${idx}`, false, "Enter the service-area slug first (e.g. mount_holly_nc)."); return; }
    setGeoFilling(idx);
    flash(`geo-${idx}`, true, "Researching…");
    try {
      const res = await authFetch(`${api}/api/clients/${clientId}/geo-facts/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service_area_slug: slug }),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      const f = d.facts || {};
      setRow("geo", idx, {
        climate: f.climate || "",
        utility_rebates: f.utility_rebates || "",
        building_stock: f.building_stock || "",
        regional_refs: f.regional_refs || "",
      });
      flash(`geo-${idx}`, true, `Filled from ${f.city || slug}${f.state ? ", " + f.state : ""} — review and Save.`);
    } catch (e) {
      flash(`geo-${idx}`, false, e.message || "Auto-fill failed");
    } finally {
      setGeoFilling(null);
    }
  };

  // ── image source-flagging save ─────────────────────────────────────
  const setImage = (idx, patch) => {
    setImages(prev => {
      const arr = prev.slice();
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  };
  const saveImage = async (idx) => {
    const img = images[idx];
    setImage(idx, { _saving: true });
    try {
      const res = await authFetch(`${api}/api/images/${img.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: img.source || "stock",
          service_type: img.service_type || "",
          location_context: img.location_context || "",
        }),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`);
      setImage(idx, { _saving: false });
      flash(`img-${idx}`, true, "Saved.");
    } catch (e) {
      setImage(idx, { _saving: false });
      flash(`img-${idx}`, false, e.message || "Save failed");
    }
  };

  if (!clientId) return null;

  const renderField = (type, idx, row, f) => {
    const common = {
      value: row[f.key] == null ? "" : row[f.key],
      onChange: e => setRow(type, idx, { [f.key]: e.target.value }),
      placeholder: f.placeholder || "",
    };
    if (f.type === "textarea") {
      return <textarea style={{ ...styles.input, minHeight: 54, resize: "vertical" }} {...common} />;
    }
    if (f.type === "date") {
      return <input type="date" style={styles.input} {...common} />;
    }
    return <input type="text" style={styles.input} {...common} />;
  };

  const SectionStatus = ({ k }) => {
    const s = status[k];
    if (!s || !s.msg) return null;
    return <span style={{ fontSize: 11, marginLeft: 10, color: s.ok ? "#22c55e" : "#ef4444", fontFamily: "'Barlow', sans-serif" }}>{s.ok ? "✓" : "✕"} {s.msg}</span>;
  };

  return (
    <div style={styles.shell}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>Client Asset Library · Keystone</div>
          <div style={styles.title}>Business Identity &amp; Assets</div>
        </div>
        <button style={styles.refreshBtn} onClick={load} disabled={loading} title="Reload assets">↻</button>
      </div>

      {error && <div style={styles.errorBanner}>Failed to load assets: {error}</div>}
      {loading && <div style={styles.loading}>Loading asset library…</div>}

      {!loading && (
        <>
          {/* ── VERIFIED FACTS ─────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionTitle}>Verified Facts</span>
              <span style={styles.sectionBlurb}>Operator-verified truths the claim gate enforces. Blank = unverified (un-assertable).</span>
            </div>
            <div style={styles.factGrid}>
              {Object.keys(factTypes).map(key => (
                <div key={key} style={styles.factRow}>
                  <div style={styles.factLabel}>{FACT_LABELS[key] || key}</div>
                  {factTypes[key] === "boolean" ? (
                    <select style={styles.input} value={facts[key] || ""} onChange={e => setFacts(f => ({ ...f, [key]: e.target.value }))}>
                      <option value="">— Unverified —</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <input
                      type={factTypes[key] === "int" ? "number" : "text"}
                      min={factTypes[key] === "int" ? 0 : undefined}
                      style={styles.input}
                      value={facts[key] || ""}
                      onChange={e => setFacts(f => ({ ...f, [key]: e.target.value }))}
                      placeholder={factTypes[key] === "int" ? "0" : ""}
                    />
                  )}
                </div>
              ))}
            </div>
            <div style={styles.sectionFoot}>
              <button style={styles.saveBtn} onClick={saveFacts} disabled={savingFacts}>{savingFacts ? "Saving…" : "Save Verified Facts"}</button>
              <SectionStatus k="facts" />
            </div>
          </div>

          {/* ── AUTHOR / BYLINE ────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionTitle}>Author / Byline</span>
              <span style={styles.sectionBlurb}>The one site author for the Article / Person schema. EEAT = data + schema.</span>
            </div>
            <div style={styles.factGrid}>
              {[
                { key: "author_name", label: "Author name", ph: "e.g. Dave Whitfield" },
                { key: "author_title", label: "Title", ph: "e.g. Owner, Master HVAC" },
                { key: "author_credentials", label: "Credentials", ph: "NC HVAC License #..." },
              ].map(a => (
                <div key={a.key} style={styles.factRow}>
                  <div style={styles.factLabel}>{a.label}</div>
                  <input type="text" style={styles.input} value={author[a.key]} placeholder={a.ph}
                    onChange={e => setAuthor(p => ({ ...p, [a.key]: e.target.value }))} />
                </div>
              ))}
            </div>

            {/* Headshot — upload the tech/owner photo (Person schema image). */}
            <div style={{ marginTop: 10, display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                border: "1px solid #2a2a2a", background: "#111",
                backgroundImage: author.author_headshot_url ? `url(${author.author_headshot_url})` : "none",
                backgroundSize: "cover", backgroundPosition: "center",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#555", fontSize: 10, textAlign: "center",
              }}>
                {author.author_headshot_url ? "" : "No\nphoto"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.factLabel}>Headshot</div>
                <label style={{
                  display: "inline-block", cursor: uploadingHeadshot ? "default" : "pointer",
                  padding: "7px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: uploadingHeadshot ? "#3a1414" : "#7a1f1f", color: "#fff",
                  border: "1px solid #c0392b", opacity: uploadingHeadshot ? 0.7 : 1,
                }}>
                  {uploadingHeadshot ? "Uploading…" : (author.author_headshot_url ? "Replace photo" : "Upload photo")}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                    style={{ display: "none" }} disabled={uploadingHeadshot}
                    onChange={e => { const f = e.target.files && e.target.files[0]; if (f) uploadHeadshot(f); e.target.value = ""; }} />
                </label>
                <div style={{ fontSize: 11, color: "#777", marginTop: 5 }}>
                  JPG / PNG / WEBP, up to 8 MB. Saved on upload — drives the author byline + Person schema.
                </div>
                <input type="text" style={{ ...styles.input, marginTop: 6, fontSize: 11 }}
                  value={author.author_headshot_url} placeholder="…or paste an image URL"
                  onChange={e => setAuthor(p => ({ ...p, author_headshot_url: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={styles.factLabel}>Bio</div>
              <textarea style={{ ...styles.input, minHeight: 54, resize: "vertical" }} value={author.author_bio}
                placeholder="Short factual bio (Person schema description)."
                onChange={e => setAuthor(p => ({ ...p, author_bio: e.target.value }))} />
            </div>
            <div style={styles.sectionFoot}>
              <button style={styles.saveBtn} onClick={saveAuthor} disabled={savingAuthor}>{savingAuthor ? "Saving…" : "Save Author"}</button>
              <SectionStatus k="author" />
            </div>
          </div>

          {/* ── FIVE LIST TABLES ───────────────────────────────────── */}
          {LIST_TYPES.map(type => {
            const schema = LIST_SCHEMAS[type];
            const rows = lists[type] || [];
            return (
              <div key={type} style={styles.section}>
                <div style={styles.sectionHead}>
                  <span style={styles.sectionTitle}>{schema.title}</span>
                  <span style={styles.sectionBlurb}>{schema.blurb}</span>
                </div>
                {rows.length === 0 && (
                  <div style={styles.emptyState}>None yet — the matching enrichment is skipped until you add one.</div>
                )}
                {rows.map((row, idx) => (
                  <div key={row.id || row._key || idx} style={styles.assetCard}>
                    <div style={styles.assetGrid}>
                      {schema.fields.map(f => (
                        <div key={f.key} style={{ gridColumn: (f.type === "textarea" ? "1 / -1" : "auto") }}>
                          <div style={styles.factLabel}>{f.label}{f.required ? " *" : ""}{f.type === "array" ? " (comma-separated)" : ""}</div>
                          {renderField(type, idx, row, f)}
                        </div>
                      ))}
                    </div>
                    <div style={styles.assetFoot}>
                      <button style={styles.saveBtnSm} onClick={() => saveRow(type, idx)} disabled={row._saving}>
                        {row._saving ? "Saving…" : row.id ? "Save" : "Save new"}
                      </button>
                      {type === "geo" && (
                        <button
                          style={{ ...styles.saveBtnSm, background: "transparent", color: "#7aa2ff", border: "1px solid #2c3a66" }}
                          onClick={() => autofillGeo(idx)}
                          disabled={geoFilling === idx}
                          title="Auto-fill climate, utility, building stock & landmarks from the service-area slug">
                          {geoFilling === idx ? "Researching…" : "✨ Auto-fill from location"}
                        </button>
                      )}
                      <button style={styles.removeBtn} onClick={() => removeRow(type, idx)}>Remove</button>
                      <SectionStatus k={`${type}-${idx}`} />
                    </div>
                  </div>
                ))}
                <button style={styles.addBtn} onClick={() => addRow(type)}>{schema.addLabel}</button>
              </div>
            );
          })}

          {/* ── IMAGE SOURCE-FLAGGING ──────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionTitle}>Image Sources</span>
              <span style={styles.sectionBlurb}>Flag each image stock vs real client photo. E3 prefers client photos, matched by service / location.</span>
            </div>
            {images.length === 0 && (
              <div style={styles.emptyState}>No images for this client. Upload images in the Image Library tab, then flag them here.</div>
            )}
            <div style={styles.imageGrid}>
              {images.map((img, idx) => (
                <div key={img.id} style={styles.imageCard}>
                  <div style={styles.imageThumbWrap}>
                    {img.storage_path
                      ? <img src={img.storage_path} alt={img.filename || "client image"} style={styles.imageThumb} />
                      : <div style={{ ...styles.imageThumb, display: "flex", alignItems: "center", justifyContent: "center", color: "#333", fontSize: 10 }}>no preview</div>}
                    <span style={{ ...styles.sourcePill, background: img.source === "client_photo" ? "rgba(34,197,94,0.18)" : "rgba(255,255,255,0.06)", color: img.source === "client_photo" ? "#4ade80" : "#888" }}>
                      {img.source === "client_photo" ? "REAL" : "STOCK"}
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: "#444", fontFamily: "'Barlow Condensed', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{img.filename || img.id}</div>
                  <select style={{ ...styles.input, marginTop: 4 }} value={img.source} onChange={e => setImage(idx, { source: e.target.value })}>
                    <option value="stock">Stock</option>
                    <option value="client_photo">Client photo (real)</option>
                  </select>
                  <input type="text" style={{ ...styles.input, marginTop: 4 }} value={img.service_type} placeholder="service_type" onChange={e => setImage(idx, { service_type: e.target.value })} />
                  <input type="text" style={{ ...styles.input, marginTop: 4 }} value={img.location_context} placeholder="location slug" onChange={e => setImage(idx, { location_context: e.target.value })} />
                  <div style={styles.assetFoot}>
                    <button style={styles.saveBtnSm} onClick={() => saveImage(idx)} disabled={img._saving}>{img._saving ? "…" : "Save"}</button>
                    <SectionStatus k={`img-${idx}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  shell: {
    background: "#0d0d0d",
    border: "1px solid #1a1a1a",
    borderRadius: 4,
    padding: "16px 18px",
    marginTop: 18,
    marginBottom: 18,
  },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14 },
  eyebrow: {
    fontSize: 10,
    color: "#d60000",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontFamily: "'Barlow Condensed', sans-serif",
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: "0.02em",
    marginTop: 2,
  },
  refreshBtn: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#888",
    fontSize: 14,
    padding: "4px 10px",
    borderRadius: 3,
    cursor: "pointer",
  },
  loading: { color: "#666", fontSize: 12, padding: "6px 0", fontStyle: "italic" },
  errorBanner: {
    color: "#fca5a5",
    fontSize: 12,
    background: "rgba(214,0,0,0.08)",
    border: "1px solid rgba(214,0,0,0.3)",
    padding: "8px 12px",
    borderRadius: 3,
    marginBottom: 10,
  },
  section: {
    background: "#0a0a0a",
    border: "1px solid #1a1a1a",
    borderRadius: 3,
    padding: "12px 14px",
    marginBottom: 12,
  },
  sectionHead: { marginBottom: 10 },
  sectionTitle: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700,
    color: "#fff",
    marginRight: 10,
  },
  sectionBlurb: { fontSize: 11, color: "#666", fontFamily: "'Barlow', sans-serif" },
  sectionFoot: { display: "flex", alignItems: "center", marginTop: 10 },
  factGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  factRow: { display: "flex", flexDirection: "column", gap: 4 },
  factLabel: {
    fontSize: 9,
    color: "#777",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'Barlow Condensed', sans-serif",
    marginBottom: 3,
  },
  input: {
    width: "100%",
    background: "#111",
    border: "1px solid #2a2a2a",
    color: "#eee",
    fontSize: 12,
    padding: "6px 8px",
    borderRadius: 3,
    fontFamily: "'Barlow', sans-serif",
    boxSizing: "border-box",
  },
  emptyState: { fontSize: 11, color: "#444", fontStyle: "italic", fontFamily: "'Barlow', sans-serif", padding: "4px 0 10px" },
  assetCard: {
    background: "#0d0d0d",
    border: "1px solid #1a1a1a",
    borderRadius: 3,
    padding: "10px 12px",
    marginBottom: 8,
  },
  assetGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 },
  assetFoot: { display: "flex", alignItems: "center", gap: 8, marginTop: 8 },
  saveBtn: {
    background: "#d60000",
    border: "1px solid #d60000",
    color: "#fff",
    fontSize: 11,
    padding: "6px 14px",
    borderRadius: 3,
    cursor: "pointer",
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  saveBtnSm: {
    background: "#d60000",
    border: "1px solid #d60000",
    color: "#fff",
    fontSize: 10,
    padding: "4px 12px",
    borderRadius: 3,
    cursor: "pointer",
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  removeBtn: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#888",
    fontSize: 10,
    padding: "4px 12px",
    borderRadius: 3,
    cursor: "pointer",
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  addBtn: {
    background: "transparent",
    border: "1px dashed #2a2a2a",
    color: "#aaa",
    fontSize: 11,
    padding: "6px 14px",
    borderRadius: 3,
    cursor: "pointer",
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginTop: 2,
  },
  imageGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 },
  imageCard: {
    background: "#0d0d0d",
    border: "1px solid #1a1a1a",
    borderRadius: 3,
    padding: 8,
  },
  imageThumbWrap: { position: "relative", marginBottom: 6 },
  imageThumb: { width: "100%", height: 90, objectFit: "cover", borderRadius: 3, display: "block", background: "#111" },
  sourcePill: {
    position: "absolute",
    top: 4,
    left: 4,
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: "0.1em",
    fontFamily: "'Barlow Condensed', sans-serif",
    padding: "1px 6px",
    borderRadius: 2,
  },
};
